import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, ActionCreator, Creator } from '@ngrx/store';
import { map } from 'rxjs/operators';
import {
  GetSessionSummaryResponse,
  PaymentStatus,
  TransactionAuthorizationResult,
} from '@arc/unified-payments-frontend/shared-payment';

/**
 * Factory that creates a reusable NgRx functional effect synthesizing the transient
 * transactionAuthorizationResults a live payment would have produced, from the first
 * transaction of a receipt-landing page's own session-summary load — feeds
 * selectPaymentStatus/selectAuthorizedPaymentDate on the confirmation screen.
 *
 * Usage (POI, POA, international — each passes its own action set):
 *
 *   export const poiReceiptLandingTransactionAuthorizationEffect =
 *     createReceiptLandingTransactionAuthorizationEffect({
 *       loadSessionSummarySuccessAction: PoiPaymentReceiptComponentActions.loadSessionSummarySuccess,
 *       updateTransactionAuthorizationResultsAction:
 *         PoiPaymentReceiptComponentActions.updateTransactionAuthorizationResults,
 *     });
 */
export function createReceiptLandingTransactionAuthorizationEffect<
  A extends Action<string> & { payload: GetSessionSummaryResponse },
>(config: {
  loadSessionSummarySuccessAction: ActionCreator<string, Creator<any[], A>>;
  updateTransactionAuthorizationResultsAction: ActionCreator<
    string,
    Creator<
      [
        {
          transactionAuthorizationResult: TransactionAuthorizationResult[];
          transactionReference: string;
        },
      ],
      Action
    >
  >;
}) {
  return createEffect(
    (actions$ = inject(Actions)) =>
      actions$.pipe(
        ofType(config.loadSessionSummarySuccessAction),
        map((action) => {
          const transaction = action.payload.payment?.transactions?.[0];
          return config.updateTransactionAuthorizationResultsAction({
            transactionAuthorizationResult: [
              {
                gpalStatus: transaction?.status as PaymentStatus,
                paymentDate: transaction?.paymentDate,
              },
            ],
            transactionReference: transaction?.transactionReference,
          }) as Action;
        }),
      ),
    { functional: true },
  );
}
